import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    InputAdornment,
    Chip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Snackbar,
    Alert,
    CircularProgress,
    Skeleton,
    Tabs,
    Tab
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import Menu from '@mui/material/Menu';
import { useSelector } from 'react-redux';

import { ReservationService } from '../../api/services/reservationService';
import { RoomService } from '../../api/services/roomService';
import ReservationDetailModal from '../../components/ReservationDetailModal';
import GroupReservationDetailModal from '../../components/GroupReservationDetailModal';
import ReservationFormModal from '../../components/ReservationFormModal';
import GroupReservationFormModal from '../../components/GroupReservationFormModal';
import GuestManagement from '../Guests/GuestManagement';
import BookingCalendar from '../../components/BookingCalendar';
import MoveRoomModal from '../../components/MoveRoomModal';
import CancelReservationModal from '../../components/CancelReservationModal';
import NoShowReservationModal from '../../components/NoShowReservationModal';
import ProcessRefundModal from '../../components/ProcessRefundModal';
import GuestFolioDrawer from './components/GuestFolioDrawer';
import CheckInReservationModal from '../../components/CheckInReservationModal';
import ReservationStatusBadge from '../../components/ReservationStatusBadge';
import CheckOutReservationModal from '../../components/CheckOutReservationModal';
import ExportReservationModal from '../../components/ExportReservationModal';
import ApproveGovernmentModal from '../../components/ApproveGovernmentModal';
import RateOverridesUI from './components/RateOverridesUI';
import { getCookie } from '../../utils/cookies';
import { useCurrency } from '../../utils/currency';
import { isUaeProperty } from '../../utils/dubaiHospitalityTax';
import ReplayIcon from '@mui/icons-material/Replay';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { extractTotalCount, standardFilterContainerSx, standardSearchInputSx, standardFilterSelectSx, standardFilterInputSx, standardResetButtonSx, standardSecondaryButtonSx } from '../../components/common/tableStyles';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && (
                <Box sx={{ mt: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default function ReservationManagement() {
    // Auth and Tenant ID
    const authData = JSON.parse(getCookie('adminAuth') || '{}');
    const tenantId = authData.tenantId || '1';
    const roleName = authData.role?.name || authData.role || '';

    // Reservations Data States
    const [reservations, setReservations] = useState([]);
    const [totalReservations, setTotalReservations] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tabValue, setTabValue] = useState(0);

    const { currency } = useCurrency();
    const { tenants, user } = useSelector((state) => state.auth);
    const tenant = tenants.find(t => String(t.tenantId) === String(tenantId) || String(t.id) === String(tenantId) || String(t._id) === String(tenantId));
    const hotelStatus = tenant?.status || 'Active';
    const isUae = currency === 'AED' || isUaeProperty(tenant);
    const [isClearanceApproving, setIsClearanceApproving] = useState(null);
    const [govApprovalModal, setGovApprovalModal] = useState({ open: false, reservation: null });

    // Check MANAGE_CHECKIN permission
    const hasManageCheckin = React.useMemo(() => {
        if (!user) return true;
        const roleName = user.role?.name || user.roleName || '';
        if (roleName === 'Super Admin' || roleName === 'Admin') return true;

        let rawPerms = [];
        if (Array.isArray(user.customPermissions) && user.customPermissions.length > 0) {
            rawPerms = user.customPermissions;
        } else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
            rawPerms = user.permissions;
        } else if (user.role && Array.isArray(user.role.permissions)) {
            rawPerms = user.role.permissions;
        }

        const perms = rawPerms.map(p => {
            if (typeof p === 'string') return p;
            if (p && typeof p === 'object') return p.key || p.name || p.code || '';
            return '';
        }).filter(Boolean);

        return perms.includes('*') || perms.includes('MANAGE_CHECKIN') || perms.includes('CAN_MANAGE_CHECKIN');
    }, [user]);

    // Filter States
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [bookingTypeFilter, setBookingTypeFilter] = useState('All');
    const [paymentStatusFilter, setPaymentStatusFilter] = useState('All');
    const [roomTypeFilter, setRoomTypeFilter] = useState('All');
    const [channelFilter, setChannelFilter] = useState('All');
    const [checkInFrom, setCheckInFrom] = useState('');
    const [checkInTo, setCheckInTo] = useState('');
    const [checkOutFrom, setCheckOutFrom] = useState('');
    const [checkOutTo, setCheckOutTo] = useState('');

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    // Setup / Selection Data States
    const [roomTypes, setRoomTypes] = useState([]);
    const [detailModal, setDetailModal] = useState({ open: false, data: null });
    const [groupDetailModal, setGroupDetailModal] = useState({ open: false, groupId: null });
    const [moveRoomModal, setMoveRoomModal] = useState({ open: false, reservation: null });
    const [cancelModal, setCancelModal] = useState({ open: false, reservation: null });
    const [noShowModal, setNoShowModal] = useState({ open: false, reservation: null });
    const [refundModal, setRefundModal] = useState({ open: false, reservation: null });
    const [checkInModal, setCheckInModal] = useState({ open: false, reservation: null });
    const [checkOutModal, setCheckOutModal] = useState({ open: false, reservation: null });
    const [folioDrawerOpen, setFolioDrawerOpen] = useState({ open: false, reservationId: null, reservation: null });
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [formModalEditData, setFormModalEditData] = useState(null);
    const [groupFormModalOpen, setGroupFormModalOpen] = useState(false);
    const [groupFormModalEditData, setGroupFormModalEditData] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    const handleQuickApproveClearance = async (res, payload = {}) => {
        const id = res.groupId || res.id || res._id;
        setIsClearanceApproving(id);
        try {
            const body = {
                approvalReference: payload.approvalReference || 'DET-2026-98124',
                notes: payload.notes || 'Verified and cleared with Dubai Police / DET portal'
            };
            await ReservationService.approveGovernmentClearance(id, tenantId, body);
            setNotification({
                open: true,
                message: `Government clearance approved for booking ${res.bookingId || id}. Status is now Confirmed.`,
                severity: 'success'
            });
            setRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error('Failed to quick-approve clearance:', error);
            const status = error?.response?.status;
            const msg = status === 403
                ? "Permission denied: You do not have the required 'MANAGE_CHECKIN' permission to approve government status."
                : (error?.response?.data?.message || 'Failed to approve government clearance.');
            setNotification({
                open: true,
                message: msg,
                severity: 'error'
            });
        } finally {
            setIsClearanceApproving(null);
        }
    };

    // Export States
    const [exportModalOpen, setExportModalOpen] = useState(false);

    // Debounce search query input (300ms)
    useEffect(() => {
        const handler = setTimeout(() => {
            setSearch(searchInput);
        }, 300);
        return () => clearTimeout(handler);
    }, [searchInput]);


    // Fetch Room Types dynamically for the roomType filter dropdown
    useEffect(() => {
        const fetchRoomTypes = async () => {
            try {
                const data = await RoomService.getRoomTypes();
                if (Array.isArray(data)) {
                    setRoomTypes(data.map(rt => rt.name || rt));
                } else if (data && Array.isArray(data.data)) {
                    setRoomTypes(data.data.map(rt => rt.name || rt));
                } else {
                    setRoomTypes(['Deluxe', 'Suite', 'Standard', 'Family']);
                }
            } catch (err) {
                console.error("Failed to load room types:", err);
                setRoomTypes(['Deluxe', 'Suite', 'Standard', 'Family']);
            }
        };
        fetchRoomTypes();
    }, [tenantId]);

    // Reset pagination to page 0 when filters change
    useEffect(() => {
        setPage(0);
    }, [search, statusFilter, bookingTypeFilter, paymentStatusFilter, roomTypeFilter, channelFilter, checkInFrom, checkInTo, checkOutFrom, checkOutTo]);

    // Fetch reservations when pagination or filters update
    useEffect(() => {
        const fetchReservations = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const params = {
                    page: page + 1, // API is 1-indexed
                    limit: rowsPerPage,
                    status: statusFilter !== 'All' ? (statusFilter === 'Checked-in' ? 'In-house' : statusFilter) : undefined,
                    bookingType: bookingTypeFilter !== 'All' ? bookingTypeFilter : undefined,
                    paymentStatus: paymentStatusFilter !== 'All' ? paymentStatusFilter : undefined,
                    roomType: roomTypeFilter !== 'All' ? roomTypeFilter : undefined,
                    channel: channelFilter !== 'All' ? channelFilter : undefined,
                    source: channelFilter !== 'All' ? channelFilter : undefined,
                    checkInFrom: checkInFrom || undefined,
                    checkInTo: checkInTo || undefined,
                    checkOutFrom: checkOutFrom || undefined,
                    checkOutTo: checkOutTo || undefined,
                    search: search || undefined
                };

                const result = await ReservationService.getAll(params, tenantId);
                if (Array.isArray(result)) {
                    setReservations(result);
                    setTotalReservations(result.length);
                } else if (result) {
                    const data = result.data || result.reservations || [];
                    setReservations(data);
                    setTotalReservations(extractTotalCount(result, data));
                } else {
                    setReservations([]);
                    setTotalReservations(0);
                }
            } catch (err) {
                console.error("Reservation list load error:", err);
                const errMsg = err.response?.data?.message || err.message || "Failed to load reservations";
                setError(errMsg);
                setNotification({ open: true, message: errMsg, severity: 'error' });
            } finally {
                setIsLoading(false);
            }
        };

        fetchReservations();
    }, [page, rowsPerPage, search, statusFilter, bookingTypeFilter, paymentStatusFilter, roomTypeFilter, channelFilter, checkInFrom, checkInTo, checkOutFrom, checkOutTo, tenantId, refreshKey]);

    const handleResetFilters = () => {
        setSearchInput('');
        setSearch('');
        setStatusFilter('All');
        setBookingTypeFilter('All');
        setPaymentStatusFilter('All');
        setRoomTypeFilter('All');
        setChannelFilter('All');
        setCheckInFrom('');
        setCheckInTo('');
        setCheckOutFrom('');
        setCheckOutTo('');
        setPage(0);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Checked-in': return 'success';
            case 'Checked-out': return 'info';
            case 'Confirmed': return 'primary';
            case 'Cancelled': return 'error';
            case 'No-show': return 'warning';
            default: return 'default';
        }
    };

    const formatRooms = (res) => {
        if (!res.rooms) return res.room?.roomNumber || res.room?.name || res.roomNumber || 'N/A';
        if (Array.isArray(res.rooms)) {
            return res.rooms.map(r => typeof r === 'object' ? (r.room?.roomNumber || r.room?.name || r.roomNumber || r.name) : r).join(', ');
        }
        return String(res.rooms);
    };

    const getNights = (res) => {
        if (res.nights !== undefined) return res.nights;
        if (res.checkIn && res.checkOut) {
            const diffTime = Math.abs(new Date(res.checkOut) - new Date(res.checkIn));
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays;
        }
        return 0;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return dateStr.split('T')[0];
    };

    const isFiltersDirty = searchInput || statusFilter !== 'All' || bookingTypeFilter !== 'All' || paymentStatusFilter !== 'All' || roomTypeFilter !== 'All' || channelFilter !== 'All' || checkInFrom || checkInTo || checkOutFrom || checkOutTo;

    const currentFilters = {
        status: statusFilter !== 'All' ? (statusFilter === 'Checked-in' ? 'In-house' : statusFilter) : undefined,
        bookingType: bookingTypeFilter !== 'All' ? bookingTypeFilter : undefined,
        paymentStatus: paymentStatusFilter !== 'All' ? paymentStatusFilter : undefined,
        roomType: roomTypeFilter !== 'All' ? roomTypeFilter : undefined,
        channel: channelFilter !== 'All' ? channelFilter : undefined,
        source: channelFilter !== 'All' ? channelFilter : undefined,
        checkInFrom: checkInFrom || undefined,
        checkInTo: checkInTo || undefined,
        checkOutFrom: checkOutFrom || undefined,
        checkOutTo: checkOutTo || undefined,
        search: search || undefined
    };

    return (
        <Box>
            {/* Header section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold">Reservations</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setExportModalOpen(true)}
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                        sx={standardSecondaryButtonSx}
                    >
                        Export Report
                    </Button>
                    <Tooltip title={hotelStatus !== 'Active' ? "Cannot create reservations while property is not Active" : ""}>
                        <span>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    setFormModalEditData(null);
                                    setFormModalOpen(true);
                                }}
                                disabled={hotelStatus !== 'Active'}
                            >
                                Create Single
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title={hotelStatus !== 'Active' ? "Cannot create reservations while property is not Active" : ""}>
                        <span>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    setGroupFormModalEditData(null);
                                    setGroupFormModalOpen(true);
                                }}
                                disabled={hotelStatus !== 'Active'}
                            >
                                Create Group
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: '#eaecf0', mb: 3 }}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
                    <Tab label="List View" />
                    <Tab label="Calendar View" />
                    <Tab label="Guest Directory" />
                    {roleName !== 'Receptionist' && <Tab label="Rate Overrides" />}
                </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
                {/* Main content paper containing filters & table */}
            {/* Advanced Filters */}
            <Box sx={{ ...standardFilterContainerSx, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1.5 }}>
                {/* Row 1: Search & Categorical Filters */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Search Guest or Booking ID..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={{ flex: { xs: '1 1 100%', sm: '1 1 240px', md: '1.5 1 260px' }, minWidth: 200, ...standardSearchInputSx }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#98a0aa', fontSize: 18 }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <FormControl size="small" sx={{ flex: '1 1 130px', minWidth: 125 }}>
                        <Select
                            value={statusFilter}
                            displayEmpty
                            onChange={(e) => setStatusFilter(e.target.value)}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="All">All Statuses</MenuItem>
                            <MenuItem value="Confirmed">Confirmed</MenuItem>
                            <MenuItem value="Checked-in">In-house</MenuItem>
                            <MenuItem value="Checked-out">Checked-out</MenuItem>
                            <MenuItem value="Cancelled">Cancelled</MenuItem>
                            <MenuItem value="No-show">No-show</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ flex: '1 1 120px', minWidth: 115 }}>
                        <Select
                            value={bookingTypeFilter}
                            displayEmpty
                            onChange={(e) => setBookingTypeFilter(e.target.value)}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="All">All Types</MenuItem>
                            <MenuItem value="Single">Single</MenuItem>
                            <MenuItem value="Group">Group</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ flex: '1 1 140px', minWidth: 130 }}>
                        <Select
                            value={paymentStatusFilter}
                            displayEmpty
                            onChange={(e) => setPaymentStatusFilter(e.target.value)}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="All">All Payments</MenuItem>
                            <MenuItem value="Paid">Paid</MenuItem>
                            <MenuItem value="Due">Due</MenuItem>
                            <MenuItem value="Refunded">Refunded</MenuItem>
                            <MenuItem value="Partially Paid">Partially Paid</MenuItem>
                            <MenuItem value="Unpaid">Unpaid</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ flex: '1 1 130px', minWidth: 125 }}>
                        <Select
                            value={channelFilter}
                            displayEmpty
                            onChange={(e) => setChannelFilter(e.target.value)}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="All">All Channels</MenuItem>
                            <MenuItem value="Direct">Direct</MenuItem>
                            <MenuItem value="OTA">OTA</MenuItem>
                            <MenuItem value="Corporate">Corporate</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ flex: '1 1 150px', minWidth: 145 }}>
                        <Select
                            value={roomTypeFilter}
                            displayEmpty
                            onChange={(e) => setRoomTypeFilter(e.target.value)}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="All">All Room Types</MenuItem>
                            {roomTypes.map((rt) => (
                                <MenuItem key={rt} value={rt}>{rt}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Row 2: Date Filters & Reset Button */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                    <TextField
                        size="small"
                        label="Check-In From"
                        type="date"
                        value={checkInFrom}
                        onChange={(e) => setCheckInFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: { xs: '1 1 45%', sm: '1 1 150px', md: '0 0 160px' }, ...standardFilterInputSx }}
                    />

                    <TextField
                        size="small"
                        label="Check-In To"
                        type="date"
                        value={checkInTo}
                        onChange={(e) => setCheckInTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: { xs: '1 1 45%', sm: '1 1 150px', md: '0 0 160px' }, ...standardFilterInputSx }}
                    />

                    <TextField
                        size="small"
                        label="Check-Out From"
                        type="date"
                        value={checkOutFrom}
                        onChange={(e) => setCheckOutFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: { xs: '1 1 45%', sm: '1 1 150px', md: '0 0 160px' }, ...standardFilterInputSx }}
                    />

                    <TextField
                        size="small"
                        label="Check-Out To"
                        type="date"
                        value={checkOutTo}
                        onChange={(e) => setCheckOutTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: { xs: '1 1 45%', sm: '1 1 150px', md: '0 0 160px' }, ...standardFilterInputSx }}
                    />

                    {isFiltersDirty && (
                        <Button
                            variant="outlined"
                            startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
                            sx={standardResetButtonSx}
                            onClick={handleResetFilters}
                        >
                            Reset
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Table list view */}
            <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Booking ID</TableCell>
                                <TableCell>Guest Name</TableCell>
                                <TableCell>Room(s)</TableCell>
                                <TableCell>Stay Dates</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Payment</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Channel</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                // Table skeleton rows while fetching data
                                [1, 2, 3, 4, 5].map((i) => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton width="60%" /></TableCell>
                                        <TableCell><Skeleton width="80%" /></TableCell>
                                        <TableCell><Skeleton width="40%" /></TableCell>
                                        <TableCell><Skeleton width="70%" /></TableCell>
                                        <TableCell><Skeleton width="50%" /></TableCell>
                                        <TableCell><Skeleton width="50%" /></TableCell>
                                        <TableCell><Skeleton width="50%" /></TableCell>
                                        <TableCell><Skeleton width="50%" /></TableCell>
                                        <TableCell align="right"><Skeleton width={30} sx={{ display: 'inline-block' }} /></TableCell>
                                    </TableRow>
                                ))
                            ) : reservations.length > 0 ? (
                                reservations.map((res) => (
                                    <TableRow key={res.id || res._id} hover>
                                        <TableCell><b>{res.bookingId || res.id || res._id}</b></TableCell>
                                        <TableCell>{res.guestName || (res.guest ? res.guest.name : 'N/A')}</TableCell>
                                        <TableCell>{formatRooms(res)}</TableCell>
                                        <TableCell>
                                             <Typography variant="body2">{formatDate(res.checkIn)} → {formatDate(res.checkOut)}</Typography>
                                             <Typography variant="caption" color="text.secondary">{getNights(res)} Nights</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <ReservationStatusBadge
                                                reservation={res}
                                                isUae={isUae}
                                                direction="column"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={res.paymentStatus || res.payment || 'Due'}
                                                size="small"
                                                variant="outlined"
                                                color={(res.paymentStatus || res.payment) === 'Paid' ? 'success' : 'warning'}
                                            />
                                        </TableCell>
                                        <TableCell>{res.bookingType || res.type || 'Single'}</TableCell>
                                        <TableCell>{res.bookingChannel || res.channel || res.bookingSource || res.source || 'Direct'}</TableCell>
                                        <TableCell align="right">
                                            {isUae && (res.governmentStatus === 'Pending' || (!res.governmentStatus && res.status === 'Pending')) && (
                                                <Tooltip title={!hasManageCheckin ? "Requires MANAGE_CHECKIN permission" : "Approve Government Status"}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            disabled={!hasManageCheckin || isClearanceApproving === (res.groupId || res.id || res._id) || ['Archived', 'Suspended'].includes(hotelStatus)}
                                                            onClick={() => setGovApprovalModal({ open: true, reservation: res })}
                                                        >
                                                            {isClearanceApproving === (res.groupId || res.id || res._id) ? (
                                                                <CircularProgress size={16} color="inherit" />
                                                            ) : (
                                                                <VerifiedUserIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => {
                                                        const isGroup = (res.bookingType || res.type || '').toLowerCase() === 'group';
                                                        if (isGroup) {
                                                            setGroupDetailModal({ open: true, groupId: res.groupId || res.id });
                                                        } else {
                                                            setDetailModal({ open: true, data: res });
                                                        }
                                                    }}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={
                                                ['Archived', 'Suspended'].includes(hotelStatus)
                                                    ? "Modifications are locked for Archived/Suspended properties"
                                                    : (res.status || '').toLowerCase() !== 'confirmed'
                                                    ? "Editing is only available for confirmed reservations"
                                                    : "Edit Reservation"
                                            }>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => {
                                                            const isGroup = (res.bookingType || res.type || '').toLowerCase() === 'group';
                                                            if (isGroup) {
                                                                setGroupFormModalEditData({ id: res.groupId || res.id });
                                                                setGroupFormModalOpen(true);
                                                            } else {
                                                                setFormModalEditData(res);
                                                                setFormModalOpen(true);
                                                            }
                                                        }}
                                                        disabled={['Archived', 'Suspended'].includes(hotelStatus) || (res.status || '').toLowerCase() !== 'confirmed'}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="View Guest Folio Ledger">
                                                <IconButton
                                                    size="small"
                                                    color="secondary"
                                                    onClick={() => setFolioDrawerOpen({ open: true, reservationId: res.id || res.bookingId || res._id, reservation: res })}
                                                >
                                                    <ReceiptLongIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {res.status !== 'No-show' && res.status !== 'Cancelled' && res.status !== 'In-house' && res.status !== 'Checked-out' && (
                                                <Tooltip title="Mark No-Show">
                                                    <IconButton
                                                        size="small"
                                                        color="warning"
                                                        onClick={() => setNoShowModal({ open: true, reservation: res })}
                                                    >
                                                        <BlockIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {res.status !== 'Cancelled' && res.status !== 'No-show' && res.status !== 'Checked-out' && res.status !== 'In-house' && (
                                                <Tooltip title="Cancel Reservation">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => setCancelModal({ open: true, reservation: res })}
                                                    >
                                                        <WarningAmberIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {res.status === 'Confirmed' && (
                                                <Tooltip title="Check In Guest">
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={() => setCheckInModal({ open: true, reservation: res })}
                                                    >
                                                        <CheckCircleIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {res.status === 'In-house' && (
                                                <Tooltip title="Check Out Guest">
                                                    <IconButton
                                                        size="small"
                                                        color="info"
                                                        onClick={() => setCheckOutModal({ open: true, reservation: res })}
                                                    >
                                                        <ExitToAppIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                                        <Typography variant="body1" color="text.secondary">No reservations found.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>

                    {/* Table Pagination */}
                    <TablePagination
                        rowsPerPageOptions={[20, 50, 100]}
                        component="div"
                        count={reservations.length === 0 ? 0 : totalReservations}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </TableContainer>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
                <BookingCalendar
                    tenantId={tenantId}
                    setNotification={setNotification}
                    onMoveRoomClick={['Archived', 'Suspended'].includes(hotelStatus) ? null : (res) => setMoveRoomModal({ open: true, reservation: res })}
                />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
                <GuestManagement />
            </TabPanel>

            {roleName !== 'Receptionist' && (
                <TabPanel value={tabValue} index={3}>
                    <RateOverridesUI tenantId={tenantId} setNotification={setNotification} />
                </TabPanel>
            )}

            {/* Read-only reservation details modal */}
            <ReservationDetailModal
                open={detailModal.open}
                onClose={() => setDetailModal({ open: false, data: null })}
                reservation={detailModal.data}
                tenantId={tenantId}
                setNotification={setNotification}
                onMoveRoomClick={['Archived', 'Suspended'].includes(hotelStatus) ? null : (res) => setMoveRoomModal({ open: true, reservation: res })}
                onEditClick={['Archived', 'Suspended'].includes(hotelStatus) ? null : (res) => {
                    if ((res?.status || '').toLowerCase() === 'confirmed') {
                        setFormModalEditData(res);
                        setFormModalOpen(true);
                    }
                }}
                onCancelClick={(res) => setCancelModal({ open: true, reservation: res })}
                onNoShowClick={(res) => setNoShowModal({ open: true, reservation: res })}
                onRefundClick={(res) => setRefundModal({ open: true, reservation: res })}
                onCheckInClick={(res) => setCheckInModal({ open: true, reservation: res })}
                onCheckOutClick={(res) => setCheckOutModal({ open: true, reservation: res })}
                onClearanceApproved={() => setRefreshKey(prev => prev + 1)}
            />

            {/* Group reservation slide-over details */}
            <GroupReservationDetailModal
                open={groupDetailModal.open}
                onClose={() => setGroupDetailModal({ open: false, groupId: null })}
                groupId={groupDetailModal.groupId}
                tenantId={tenantId}
                setNotification={setNotification}
                onClearanceApproved={() => setRefreshKey(prev => prev + 1)}
            />

            <MoveRoomModal
                open={moveRoomModal.open}
                onClose={() => setMoveRoomModal({ open: false, reservation: null })}
                reservation={moveRoomModal.reservation}
                tenantId={tenantId}
                onSuccess={() => {
                    setRefreshKey(prev => prev + 1);
                    setDetailModal({ open: false, data: null });
                }}
                setNotification={setNotification}
            />



            <CancelReservationModal
                open={cancelModal.open}
                onClose={() => setCancelModal({ open: false, reservation: null })}
                reservation={cancelModal.reservation}
                tenantId={tenantId}
                onSuccess={() => {
                    setRefreshKey(prev => prev + 1);
                    setDetailModal({ open: false, data: null });
                }}
                setNotification={setNotification}
            />

            <NoShowReservationModal
                open={noShowModal.open}
                onClose={() => setNoShowModal({ open: false, reservation: null })}
                reservation={noShowModal.reservation}
                tenantId={tenantId}
                onSuccess={() => {
                    setRefreshKey(prev => prev + 1);
                    setDetailModal({ open: false, data: null });
                }}
                setNotification={setNotification}
            />

            <ProcessRefundModal
                open={refundModal.open}
                onClose={() => setRefundModal({ open: false, reservation: null })}
                reservation={refundModal.reservation}
                tenantId={tenantId}
                onSuccess={() => {
                    setRefreshKey(prev => prev + 1);
                    setDetailModal({ open: false, data: null });
                }}
                setNotification={setNotification}
            />

            <CheckInReservationModal
                open={checkInModal.open}
                onClose={() => setCheckInModal({ open: false, reservation: null })}
                reservation={checkInModal.reservation}
                tenantId={tenantId}
                onSuccess={() => {
                    setRefreshKey(prev => prev + 1);
                    setDetailModal({ open: false, data: null });
                }}
                setNotification={setNotification}
            />

            <CheckOutReservationModal
                open={checkOutModal.open}
                onClose={() => setCheckOutModal({ open: false, reservation: null })}
                reservation={checkOutModal.reservation}
                tenantId={tenantId}
                onSuccess={() => {
                    setRefreshKey(prev => prev + 1);
                    setDetailModal({ open: false, data: null });
                }}
                setNotification={setNotification}
            />

            {/* Create Reservation Form Modal */}
            <ReservationFormModal
                open={formModalOpen}
                editData={formModalEditData}
                onClose={() => {
                    setFormModalOpen(false);
                    setFormModalEditData(null);
                }}
                onSuccess={(message) => {
                    setFormModalOpen(false);
                    setFormModalEditData(null);
                    setNotification({ open: true, message: message || 'Reservation created successfully', severity: 'success' });
                    setRefreshKey(prev => prev + 1);
                }}
            />

            {/* Create Group Reservation Form Modal */}
            <GroupReservationFormModal
                open={groupFormModalOpen}
                editData={groupFormModalEditData}
                onClose={() => {
                    setGroupFormModalOpen(false);
                    setGroupFormModalEditData(null);
                }}
                onSuccess={(message) => {
                    setGroupFormModalOpen(false);
                    setGroupFormModalEditData(null);
                    setNotification({ open: true, message: message || 'Group reservation successfully processed', severity: 'success' });
                    setRefreshKey(prev => prev + 1);
                }}
            />

            <GuestFolioDrawer
                open={folioDrawerOpen.open}
                onClose={() => setFolioDrawerOpen({ open: false, reservationId: null, reservation: null })}
                reservationId={folioDrawerOpen.reservationId}
                reservation={folioDrawerOpen.reservation}
                tenantId={tenantId}
            />

            <ExportReservationModal
                open={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                tenantId={tenantId}
                currentFilters={currentFilters}
                setNotification={setNotification}
            />

            {/* Government Approval Modal */}
            <ApproveGovernmentModal
                open={govApprovalModal.open}
                onClose={() => setGovApprovalModal({ open: false, reservation: null })}
                reservation={govApprovalModal.reservation}
                tenantId={tenantId}
                onSuccess={() => setRefreshKey(prev => prev + 1)}
                setNotification={setNotification}
            />

            {/* Error notifications snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
